import { ComponentRef, EventEmitter, Injectable, Injector, NgZone, inject } from '@angular/core';
import { Overlay, OverlayRef, OverlayConfig, ConnectedPosition } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Subject, fromEvent } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  CARRIER_PRODUCT_FLYOUT_DATA,
  CarrierProductFlyOutComponent,
  CarrierProductFlyOutData,
} from './carrier-product-flyout.component';
import type { PickerApplyResult, PickerData } from './company-product-picker.types';

interface OpenOptions {
  anchorEl: HTMLElement;
  data: PickerData;
  focusedCarrierId: string;
  initialSelectedProductIds: string[];
  /** Reactive read of the rail's "Filter the filters" query. */
  query: () => string;
  /** Live-emits on every selection change in the fly-out. */
  onSelectionChange: (r: PickerApplyResult) => void;
  /** Optional callback fired when the fly-out closes (any cause). */
  onClose?: () => void;
}

/**
 * CarrierProductFlyOutService — opens / re-anchors / closes the per-carrier
 * products fly-out. Single-instance: re-anchoring on a different carrier
 * row replaces contents in place instead of opening a second panel.
 *
 * Close behaviors:
 *   · Esc (CDK Overlay keydown)
 *   · Outside-click via a global pointerdown listener (we do NOT use a CDK
 *     backdrop, because a backdrop would block clicks to the rail's
 *     other carrier rows — we want re-anchoring on those clicks instead)
 *   · Caller may call close() explicitly (collapse Carrier group, tab
 *     switch, or click the focused carrier row again)
 */
@Injectable({ providedIn: 'root' })
export class CarrierProductFlyOutService {
  private overlay = inject(Overlay);
  private parentInjector = inject(Injector);
  private zone = inject(NgZone);

  private overlayRef: OverlayRef | null = null;
  private componentRef: ComponentRef<CarrierProductFlyOutComponent> | null = null;
  private anchorEl: HTMLElement | null = null;
  private currentCarrierId: string | null = null;
  private currentOnClose: (() => void) | null = null;
  private outsideClick$ = new Subject<void>();

  /** True while the fly-out is rendered. */
  isOpen(): boolean {
    return this.overlayRef != null;
  }

  /** Carrier currently focused inside the open fly-out (or null). */
  focusedCarrierId(): string | null {
    return this.currentCarrierId;
  }

  open(opts: OpenOptions): void {
    if (this.overlayRef) {
      // Already open — re-anchor instead of stacking. If it's the same
      // carrier, this is the "click the focused row again → close" path.
      if (this.currentCarrierId === opts.focusedCarrierId && this.anchorEl === opts.anchorEl) {
        this.close();
        return;
      }
      this.reanchor(opts);
      return;
    }

    const positions: ConnectedPosition[] = [
      // Primary: open to the right of the row, top-aligned
      { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 8 },
      // Bottom-overflow fallback: align fly-out bottom to row bottom
      { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: 8 },
      // Last resort: above
      { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetX: 8, offsetY: -4 },
    ];

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(opts.anchorEl)
      .withPositions(positions)
      .withFlexibleDimensions(false)
      .withPush(true)
      .withViewportMargin(8);

    const config = new OverlayConfig({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
      panelClass: 'carrier-product-flyout-panel',
    });

    const overlayRef = this.overlay.create(config);
    this.overlayRef = overlayRef;
    this.anchorEl = opts.anchorEl;
    this.currentCarrierId = opts.focusedCarrierId;
    this.currentOnClose = opts.onClose ?? null;

    // Bridge the (selectionChange) Output of the fly-out's inner picker to
    // the caller's handler. EventEmitter is reused across re-anchors so a
    // single subscription survives the lifetime of this open.
    const selectionEmitter = new EventEmitter<PickerApplyResult>();
    selectionEmitter.subscribe((r) => opts.onSelectionChange(r));

    const data: CarrierProductFlyOutData = {
      data: opts.data,
      focusedCarrierId: opts.focusedCarrierId,
      initialSelectedProductIds: opts.initialSelectedProductIds,
      query: opts.query,
      selectionChange: selectionEmitter,
    };

    const injector = Injector.create({
      parent: this.parentInjector,
      providers: [{ provide: CARRIER_PRODUCT_FLYOUT_DATA, useValue: data }],
    });

    const portal = new ComponentPortal(CarrierProductFlyOutComponent, null, injector);
    this.componentRef = overlayRef.attach(portal);

    // Esc → close
    overlayRef.keydownEvents().subscribe((e) => {
      if (e.key === 'Escape') this.close();
    });

    // Outside-click → close. Run outside Angular so we don't trigger CD
    // on every pointerdown.
    //
    // The ONE exception is a carrier row. The rail owns that click and will
    // either re-anchor this panel to the newly clicked carrier or toggle it
    // shut; and the row's checkbox lives inside that same button, selecting
    // the whole carrier while the panel stays open to show the result.
    //
    // Everything else closes, INCLUDING the rest of the rail. This used to
    // exempt the whole rail, so clicking another filter group, the rail
    // background or the carrier list's scrollbar left the panel sitting there
    // still bound to a carrier the user had scrolled well past.
    this.outsideClick$ = new Subject<void>();
    this.zone.runOutsideAngular(() => {
      fromEvent<PointerEvent>(document, 'pointerdown', { capture: true })
        .pipe(takeUntil(this.outsideClick$))
        .subscribe((ev) => {
          const target = ev.target as HTMLElement | null;
          if (!target) return;
          // Inside the fly-out itself? Ignore.
          if (this.overlayRef?.overlayElement.contains(target)) return;
          // A carrier row in the rail that owns this panel? Ignore, per above.
          // Scoped to the owner rail so a .co-row in some other picker cannot
          // keep this one alive.
          const ownerRail = this.anchorEl?.closest('aside.rail');
          const carrierRow = target.closest('.co-row');
          if (carrierRow && ownerRail?.contains(carrierRow)) return;
          this.zone.run(() => this.close());
        });
    });
  }

  /** Re-anchor to a different carrier without closing. */
  reanchor(opts: OpenOptions): void {
    if (!this.overlayRef || !this.componentRef) return;

    this.anchorEl = opts.anchorEl;
    this.currentCarrierId = opts.focusedCarrierId;

    // Re-aim the position strategy at the new row.
    const positions: ConnectedPosition[] = [
      { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 8 },
      { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: 8 },
      { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetX: 8, offsetY: -4 },
    ];
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(opts.anchorEl)
      .withPositions(positions)
      .withFlexibleDimensions(false)
      .withPush(true)
      .withViewportMargin(8);
    this.overlayRef.updatePositionStrategy(positionStrategy);

    // Update the picker's focused carrier + initial selection in place.
    this.componentRef.instance.setFocusedCarrier(
      opts.focusedCarrierId,
      opts.initialSelectedProductIds,
    );
  }

  /** Update the external query (rail's filter-search input). */
  setQuery(q: string): void {
    this.componentRef?.instance.setQuery(q);
  }

  /** Re-seed the currently-open fly-out's selection without changing which
   *  carrier is focused or repositioning the panel. Needed when the
   *  selection changes via a different source while the fly-out is open —
   *  e.g. the rail's own carrier-level checkbox (which toggles the whole
   *  carrier directly, bypassing rowClick/reanchor entirely) — otherwise
   *  the fly-out keeps showing its stale pre-toggle checkboxes. */
  refreshSelection(selectedProductIds: string[]): void {
    if (!this.overlayRef || !this.componentRef || !this.currentCarrierId) return;
    this.componentRef.instance.setFocusedCarrier(this.currentCarrierId, selectedProductIds);
  }

  close(): void {
    if (!this.overlayRef) return;
    this.outsideClick$.next();
    this.outsideClick$.complete();
    const onClose = this.currentOnClose;
    this.overlayRef.dispose();
    this.overlayRef = null;
    this.componentRef = null;
    this.anchorEl = null;
    this.currentCarrierId = null;
    this.currentOnClose = null;
    onClose?.();
  }
}
